import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { Timestamp } from '@framework/Timestamp';
import { useStickToBottomScroll } from '@user/pages/ViewModelPage/AIAssistant/useStickToBottomScroll';
import { mcpBridgeLogAtom, type MCPBridgeLogLevel } from './mcpBridgeLogAtom';

// level は MCPBridgeLogLevel の全値を網羅する必要がある。
// 未対応の level を追加した場合、このオブジェクトリテラルの型エラーでコンパイル時に検知できる。
const LEVEL_CLASS_NAME: Record<MCPBridgeLogLevel, string> = {
  success: 'text-green-400',
  info: 'text-gray-300',
  warn: 'text-yellow-300',
  error: 'text-red-400',
};

function levelClassName(level: MCPBridgeLogLevel): string {
  return LEVEL_CLASS_NAME[level];
}

// info は通常ログのためラベルを表示しない。success/warn/error のみユーザーに知らせるべき情報としてラベル表示する。
// level は MCPBridgeLogLevel の全値を網羅する必要がある。未対応の level を追加した場合、型エラーでコンパイル時に検知できる。
const LEVEL_LABEL: Record<MCPBridgeLogLevel, string | null> = {
  success: 'SUCCESS',
  info: null,
  warn: 'WARN',
  error: 'ERROR',
};

function levelLabel(level: MCPBridgeLogLevel): string | null {
  return LEVEL_LABEL[level];
}

function formatLogTimestamp(timestamp: number): string {
  const t = Timestamp.fromUnixTimestamp(timestamp);
  if (!t.isValid()) {
    // 不正な timestamp（NaN 等）を "NaN:NaN:NaN" のまま表示しないためのフォールバック
    return '--:--:--';
  }
  return t.format('HH:mm:ss');
}

export function MCPBridgeLogView() {
  const { t } = useTranslation();
  const entries = useAtomValue(mcpBridgeLogAtom);
  const { scrollContainerRef, scrollContentRef, onScroll } = useStickToBottomScroll();

  if (entries.length === 0) {
    return (
      <div className="min-h-[100px] grow rounded bg-gray-900 p-2">
        <span className="font-mono text-xs text-gray-300">{t('viewModel.mcpBridge.notConnected')}</span>
      </div>
    );
  }

  return (
    <div
      ref={scrollContainerRef}
      onScroll={onScroll}
      className="min-h-[100px] grow overflow-auto rounded bg-gray-900 p-2"
    >
      <div ref={scrollContentRef} className="flex flex-col gap-2">
        {entries.map((entry) => {
          if (entry.kind === 'speech') {
            return (
              <div key={entry.id} className="font-mono text-xs">
                <div className="text-gray-400">{formatLogTimestamp(entry.timestamp)}</div>
                <div className="text-gray-300">「{entry.message}」</div>
              </div>
            );
          }
          const label = levelLabel(entry.level);
          return (
            <div key={entry.id} className="font-mono text-xs">
              <div className="text-gray-400">
                {formatLogTimestamp(entry.timestamp)}
                {label !== null && <span className={`ml-1 font-semibold ${levelClassName(entry.level)}`}>{label}</span>}
              </div>
              <div className="text-gray-300">{entry.message}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
